// Constants
import { ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { Store } from "../Store";
// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
// Utilities
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
/**
 * Performance collection store
 */ export class PerformanceCollectionStore extends EventPublishingStore {
    get name() {
        return Store.PerformanceCollection;
    }
    /**
     * get the view render state
     * @param viewId view name and partition key
     */ getViewRenderState(viewId) {
        return this.viewRenderStates[toViewIdString(viewId)] ?? null;
    }
    /**
     * Get the pending (unresolved) tracked interaction for a given target view.
     * Returns null if no pending interaction exists for the view.
     */ getPendingInteractionByViewId(viewId) {
        return this.trackedInteractions.get(toViewIdString(viewId)) ?? null;
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.ViewRenderAction:
                storeChanged = this.handleViewRenderAction(action);
                break;
            case ActionType.StartTrackedInteractionAction:
                storeChanged = this.handleStartTrackedInteractionAction(action);
                break;
            case ActionType.CompleteTrackedInteractionAction:
                storeChanged = this.handleCompleteTrackedInteractionAction(action);
                break;
            case ActionType.CancelTrackedInteractionAction:
                storeChanged = this.handleCancelTrackedInteractionAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    /**
     * Add a tracked interaction to the store.
     * Reads completionStrategy from the action to support both viewRender and manual strategies.
     * Cleans up any previous entries for the same target view (approach A cleanup).
     */ handleStartTrackedInteractionAction(action) {
        this.trackedInteractions.set(toViewIdString(action.targetViewId), {
            completionStrategy: action.completionStrategy,
            name: action.name,
            startTime: action.startTime,
            targetViewId: action.targetViewId,
            extraData: action.extraData
        });
        return true;
    }
    /**
     * Complete a tracked interaction by removing it from the map.
     * The AC has already read the entry and logged telemetry before dispatching this action.
     */ handleCompleteTrackedInteractionAction(action) {
        return this.trackedInteractions.delete(toViewIdString(action.targetViewId));
    }
    /**
     * Cancel tracking for a view: remove any pending interaction and reset its render state.
     * Resetting the (otherwise monotonic) render state is what allows a future open of the
     * same long-lived view to be measured again. Used when the measured surface is disposed.
     */ handleCancelTrackedInteractionAction(action) {
        const key = toViewIdString(action.targetViewId);
        const hadPending = this.trackedInteractions.delete(key);
        const hadRenderState = this.viewRenderStates[key] != null;
        if (hadRenderState) {
            this.viewRenderStates[key] = null;
        }
        return hadPending || hadRenderState;
    }
    /**
     * Update the render state for a view.
     * The AC is responsible for checking pending interactions and logging telemetry
     * before dispatching this action.
     */ handleViewRenderAction(action) {
        const current = this.getViewRenderState(action.viewId);
        if (current === null || action.state > current) {
            this.loggers.performanceMarker.mark(MarkerTag.ViewRender, {
                ...action.extraData ?? {},
                viewName: action.viewId.name,
                viewData: ViewRenderState[action.state]
            });
            this.viewRenderStates[toViewIdString(action.viewId)] = action.state;
            return true;
        }
        return false;
    }
    /**
     * Instantiate a new instance of PerformanceCollectionStore
     * @param loggers The loggers for telemetry
     */ constructor(loggers){
        super(), this.loggers = loggers, /**
     * All pending interactions keyed by `toViewIdString(targetViewId)`.
     * Both viewRender and manual interactions use the same keying strategy.
     * The AC reads pending entries and logs telemetry when the interaction completes.
     */ this.trackedInteractions = new Map();
        this.viewRenderStates = {};
    }
}
